import { useEffect, useRef, useState } from 'react';

import { BookingApi } from '@/api';
import { IcDeleteTrash, IcImage, IcNote, IcUpload } from '@/assets/images';
import { FilePreviewModal } from '@/components';
import { Spinner } from '@/components/TailwindComponents/base/Spinner';
import { cn } from '@/components/TailwindComponents/lib/utils';
import { useFetchSasToken } from '@/hooks';
import { IFile } from '@/models';
import { DeleteFileModal } from '@/pages/JMS/Managament/components/modals/DeleteFileModal';
import { TabName, useBookingStore } from '@/pages/JMS/store/BookingStore';
import { previewPdf, uploadFiles } from '@/services';
import { getFileUrlWithValidToken } from '@/utils';

import { CameraAndUploadModal } from '../modals/CameraAndUploadModal';
interface FilesViewAndUploadProps {
  value: IFile[];
  handleUpload?: (files: IFile[]) => void;
  handleError?: (error: string) => void;
  viewLimit?: number;
  label?: string;
  allowedFileTypes?: string;
  tab?: string;
  disabledDelete?: boolean;
  disabled?: boolean;
  isModal?: boolean;
}

export const FilesViewAndUpload = ({
  handleUpload,
  handleError,
  allowedFileTypes = 'image/*',
  tab,
  value,
  disabledDelete,
  label,
  disabled,
  isModal,
}: FilesViewAndUploadProps) => {
  const { booking, setBooking } = useBookingStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<IFile[]>(value || []);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]); // <-- NEW
  const [deleteFileState, setDeleteFileState] = useState<{
    isModalOpen: boolean;
    file: IFile | undefined;
  }>({ isModalOpen: false, file: undefined });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const sasToken = useFetchSasToken(files);

  const handleFilesUploaded = (newFiles: IFile[]) => {
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    handleUpload?.(updatedFiles);
    setIsUploadModalOpen(false);
  };

  const openUploadModal = () => {
    if (disabled) return;
    setIsUploadModalOpen(true);
  };

  const handleModalUploadBtnClick = () => {
    if (disabled) return;
    setIsUploadModalOpen(false);
    fileInputRef.current?.click();
  };

  const handleUploadBtnClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const [fileViewModal, setFileViewModal] = useState<{
    open: boolean;
    file?: IFile;
  }>({
    open: false,
  });

  const handleCameraUpload = async (file: File | undefined) => {
    if (!file) return;
    const iFile: IFile = {
      name: file.name,
      size: file.size,
      mimeType: file.type,
      file,
    };
    try {
      const uploaded = await uploadFiles([iFile]);
      handleFilesUploaded(uploaded);
    } catch (err) {
      handleError?.((err as Error).message);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const fileList = event.target.files;
    if (!fileList) return;

    const newFileItems: IFile[] = Array.from(fileList).map((file) => ({
      name: file.name,
      size: file.size,
      mimeType: file.type,
      file,
    }));

    const allowed = allowedFileTypes.split(',').map((t) => t.trim());
    const invalidFiles = newFileItems.filter(
      (file) => !allowed.includes(file.mimeType) && allowed[0] !== '*/*',
    );
    if (invalidFiles.length) {
      handleError?.('File format is not supported, try another');
      return;
    }

    setFiles((prev) => [...prev, ...newFileItems]);
    setUploadingFiles((prev) => [...prev, ...newFileItems.map((f) => f.name)]);

    try {
      const uploaded = await uploadFiles(newFileItems); // returns IFile[]
      setFiles((prev) =>
        prev.map((f) => {
          const replacement = uploaded.find((u) => u.name === f.name);
          return replacement ?? f;
        }),
      );

      setUploadingFiles((prev) =>
        prev.filter((name) => !uploaded.some((u) => u.name === name)),
      );

      if (handleUpload) {
        if (typeof handleUpload === 'function') {
          handleUpload([...files, ...uploaded]);
        }
      }
    } catch (err) {
      handleError?.((err as Error).message);
      // Clean failed placeholders
      setFiles((prev) =>
        prev.filter((f) => !newFileItems.some((n) => n.name === f.name)),
      );
      setUploadingFiles((prev) =>
        prev.filter((name) => !newFileItems.some((n) => n.name === name)),
      );
    }
  };

  const handleDeleteFile = (file: IFile) => {
    if (uploadingFiles.includes(file.name)) return;

    setDeleteFileState((prevState) => ({
      ...prevState,
      isModalOpen: false,
    }));

    if (!file._id || tab === TabName.IDENTITY) {
      const newFiles = files.filter((f) => f.url !== file.url);
      setFiles(newFiles);
      handleUpload?.(newFiles);
      return;
    }

    setDeleteFileState({ file, isModalOpen: true });
  };

  const handleDeleteSavedFile = async (reason: string, file?: IFile) => {
    if (!file) return;

    switch (tab) {
      case TabName.FINGERPRINTS: {
        const { data: updatedBooking } =
          await BookingApi.deleteFingerprintsFile(
            reason,
            booking?._id as string,
            file._id as string,
          );
        setBooking(updatedBooking);
        setDeleteFileState({ isModalOpen: false, file: undefined });
        break;
      }
      case 'wanted-person': {
        const newFiles = files.filter((f) => f.url !== file.url);
        setFiles(newFiles);
        handleUpload?.(newFiles);
        setDeleteFileState({ isModalOpen: false, file: undefined });
        return;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    setFiles(value || []);
  }, [value]);

  return (
    <div className="flex gap-2 p-2 w-full">
      {files.length === 0 &&
        (disabled ? (
          <div className="flex flex-col items-center justify-center h-[231px] w-[218px] gap-2 p-2 ring-gray-200 ring-1 rounded-[6px] ring-offset-2">
            <IcImage className="h-8 w-8" />
            <div className="text-[14px] text-gray-400">
              No uploaded images yet
            </div>
          </div>
        ) : (
          <div className="flex text-sm">
            <div
              onClick={isModal ? openUploadModal : handleUploadBtnClick}
              className={cn(
                'flex items-center justify-center gap-2 h-[231px] w-[218px] bg-gray-100 border-2 border-dashed rounded-[6px]',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <div className="flex flex-col justify-center text-center font-semibold text-[14px]">
                <div className="flex justify-center w-full pb-2">
                  <IcUpload className="h-8 w-8" />
                </div>
                <div>Drop a file here to upload, or</div>
                <div
                  className={cn(
                    'text-red-500',
                    disabled ? 'text-gray-400' : 'text-red-500',
                  )}
                >
                  click here to browse
                </div>
              </div>
            </div>
          </div>
        ))}

      {files.length > 0 && (
        <div className="flex gap-2 w-full p-2 ring-gray-200 ring-1 rounded-[6px] ring-offset-2 overflow-x-auto">
          <div className="flex flex-col gap-1">
            <div className="flex gap-2">
              {files.map((file, idx) => {
                const fileUrl = getFileUrlWithValidToken(file, sasToken);
                const isUploading = uploadingFiles.includes(file.name);

                return (
                  <div
                    key={`${file.name}-${idx}`}
                    className="flex flex-col p-2 gap-2 w-[198px] h-[225px] bg-gray-100 rounded-[6px]"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span
                        className="truncate max-w-[160px] text-black font-semibold text-[14px]"
                        title={file.name}
                      >
                        {file.name}
                      </span>
                      {!isUploading && !disabledDelete && !disabled && (
                        <span
                          title={`Delete ${file.name}`}
                          className="text-red-500 hover:text-red-600 cursor-pointer ml-2 flex-shrink-0"
                          onClick={() => handleDeleteFile(file)}
                        >
                          <IcDeleteTrash />
                        </span>
                      )}
                    </div>

                    <div className="flex w-full h-full items-center justify-center bg-white rounded-[6px]">
                      {isUploading ? (
                        <Spinner>Uploading...</Spinner>
                      ) : file.mimeType.includes('image') ? (
                        <img
                          src={fileUrl}
                          alt={file.name}
                          className="h-[177px] w-[198px] rounded-md object-contain"
                          loading="lazy"
                          onClick={() => setFileViewModal({ open: true, file })}
                        />
                      ) : (
                        <div
                          className="flex w-full h-full items-center justify-center rounded-md cursor-pointer text-[#EE3E5C]"
                          onClick={() => previewPdf(file)}
                          title={`Click to view, [${file.name}], in new tab`}
                        >
                          <IcNote width={60} height={60} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <DeleteFileModal
                key={deleteFileState.file?.name}
                file={deleteFileState.file}
                isModalOpen={deleteFileState.isModalOpen}
                onConfirm={handleDeleteSavedFile}
                onCancel={() =>
                  setDeleteFileState((prevState) => ({
                    ...prevState,
                    isModalOpen: false,
                  }))
                }
              />
              <FilePreviewModal
                open={fileViewModal.open}
                file={fileViewModal.file}
                onClose={() => {
                  setFileViewModal({ open: false });
                }}
              />
            </div>
            <div
              className={cn(
                'flex gap-2 items-center cursor-pointer',
                disabled ? 'hidden' : 'block',
              )}
              onClick={isModal ? openUploadModal : handleUploadBtnClick}
            >
              <IcUpload className="h-3 w-3" />
              <span className="text-[14px] text-red-500">
                {label || 'Upload new files'}
              </span>
            </div>
          </div>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileUpload}
        accept={allowedFileTypes}
        multiple
        disabled={disabled}
      />

      <CameraAndUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleModalUploadBtnClick}
        allowedFileTypes={allowedFileTypes}
        onCameraUpload={handleCameraUpload}
      />
    </div>
  );
};
